if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        this.cart = this.getCart();
        if (this.cart) {
          formData.append('sections', this.getCartSectionIds(this.cart));
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement?.(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        try {
          const response = await fetch(`${routes.cart_add_url}`, config).then((response) => response.json());

          if (response.status) {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: variantId,
              errors: response.errors || response.description,
              message: response.message,
            });
            this.handleErrorMessage(response.description);
            this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
            linesUpdateDeferred?.reject(new Error(response.description || response.message));

            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (!soldOutMessage) return;
            this.submitButton.setAttribute('aria-disabled', true);
            this.submitButtonText.classList.add('hidden');
            soldOutMessage.classList.remove('hidden');
            this.error = true;
            return;
          } else if (!this.cart) {
            this.resolveCartLinesUpdate(linesUpdateDeferred);
            window.location = window.routes.cart_url;
            return;
          }

          this.resolveCartLinesUpdate(linesUpdateDeferred);
          this.error = false;

          await this.renderUpdatedCart(response);
          this.publishCartUpdate(variantId, response);
        } catch (e) {
          console.error(e);
          this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
          linesUpdateDeferred?.reject(e);
        } finally {
          this.submitButton.classList.remove('loading');
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading__spinner').classList.add('hidden');

          CartPerformance.measureFromEvent("add:user-action", evt);
        }
      }

      getCart() {
        if (this.cart && document.documentElement.contains(this.cart)) return this.cart;
        this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        return this.cart;
      }

      getCartSectionIds(cart) {
        if (cart && typeof cart.getSectionsToRender === 'function') {
          return cart.getSectionsToRender().map((section) => section.id);
        }

        if (cart?.tagName?.toLowerCase() === 'cart-drawer') return ['cart-drawer', 'cart-icon-bubble'];
        return ['cart-notification-product', 'cart-notification-button', 'cart-icon-bubble'];
      }

      async fetchCartWithSections(cart) {
        const sectionIds = this.getCartSectionIds(cart);
        const sectionsUrl = new URL(window.routes.cart_url, window.location.origin);
        sectionsUrl.searchParams.set('sections', sectionIds.join(','));
        sectionsUrl.searchParams.set('sections_url', window.location.pathname);

        const sections = await fetch(sectionsUrl.toString(), {
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
        }).then((response) => response.json());

        const cartState = await fetch(`${window.routes.cart_url}.js`, {
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
        }).then((response) => response.json());

        return {
          ...cartState,
          sections,
        };
      }

      async renderUpdatedCart(response) {
        this.cart = this.getCart();
        if (!this.cart) {
          window.location = window.routes.cart_url;
          return;
        }

        if (!response.sections) {
          response = await this.fetchCartWithSections(this.cart);
        }

        const quickAddModal = this.closest('quick-add-modal');
        const render = async () => {
          try {
            CartPerformance.measure("add:paint-updated-sections", () => {
              if (typeof this.cart.renderContents === 'function') {
                this.cart.renderContents(response);
              } else if (typeof this.cart.open === 'function') {
                this.cart.open();
              }
            });
          } catch (error) {
            const fallbackResponse = await this.fetchCartWithSections(this.cart);
            if (typeof this.cart.renderContents === 'function') {
              this.cart.renderContents(fallbackResponse);
            } else if (typeof this.cart.open === 'function') {
              this.cart.open();
            }
          }
        };

        if (quickAddModal) {
          document.body.addEventListener('modalClosed', () => setTimeout(() => render()), { once: true });
          quickAddModal.hide(true);
          return;
        }

        await render();
      }

      publishCartUpdate(variantId, response) {
        if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

        const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'product-form',
          productVariantId: variantId,
          cartData: response,
        })
          .then(() => {
            CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
          })
          .catch((error) => {
            console.warn('[product-form] Cart subscribers failed after drawer render.', error);
          });
      }
      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
